import React, { useState } from 'react';
import { Plus, X, ChevronDown } from 'lucide-react';
import { TOOL_FLAG_SUGGESTIONS } from '../constants/toolGraph';

const CustomFlagsInput = ({ tool, value = {}, onChange }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Helper functions for AI assistant special fields
  const getInputType = (key) => {
    if (key === 'api_key') return 'password';
    if (key === 'output_data' || key === 'prompt') return 'textarea';
    return 'text';
  };
  
  const getPlaceholder = (key) => {
    switch (key) {
      case 'prompt':
        return 'Enter your system prompt for the LLM...';
      case 'api_key':
        return 'sk-or-v1-...';
      case 'output_data':
        return '{"field": "string", "status": "boolean"}';
      case 'chunk_size':
        return '50';
      case 'temperature':
        return '0.0';
      case 'timeout':
        return '60';
      case 'max_retries':
        return '2';
      default:
        return 'value';
    }
  };
  
  // Use internal state to manage flags array (including empty ones)
  const [flagsArray, setFlagsArray] = useState(() => {
    return Object.entries(value).map(([key, val]) => ({ key, value: val }));
  });
  
  // Sync with parent value changes
  React.useEffect(() => {
    const newFlagsArray = Object.entries(value).map(([key, val]) => ({ key, value: val }));
    // Only update if the value actually changed
    if (JSON.stringify(newFlagsArray) !== JSON.stringify(flagsArray.filter(f => f.key))) {
      setFlagsArray(newFlagsArray);
    }
  }, [value]);
  
  const handleAddFlag = () => {
    const newFlags = [...flagsArray, { key: '', value: '' }];
    setFlagsArray(newFlags);
  };
  
  const handleRemoveFlag = (index) => {
    const newFlags = flagsArray.filter((_, i) => i !== index);
    setFlagsArray(newFlags);
    updateFlags(newFlags);
  };
  
  const handleKeyChange = (index, newKey) => {
    const newFlags = [...flagsArray];
    newFlags[index].key = newKey;
    setFlagsArray(newFlags);
    updateFlags(newFlags);
  };
  
  const handleValueChange = (index, newValue) => {
    const newFlags = [...flagsArray];
    newFlags[index].value = newValue;
    setFlagsArray(newFlags);
    updateFlags(newFlags);
  };
  
  const updateFlags = (flagsArr) => {
    // Convert array back to object, filtering out empty keys
    const flagsObj = {};
    flagsArr.forEach(flag => {
      if (flag.key) {
        flagsObj[flag.key] = flag.value;
      }
    });
    onChange(flagsObj);
  };
  
  const handleSuggestionClick = (suggestion) => {
    const newFlags = [...flagsArray, { key: suggestion.key, value: suggestion.value }];
    setFlagsArray(newFlags);
    updateFlags(newFlags);
    setShowSuggestions(false);
  };
  
  const suggestions = TOOL_FLAG_SUGGESTIONS[tool] || [];
  const usedKeys = flagsArray.map(f => f.key);
  const availableSuggestions = suggestions.filter(s => !usedKeys.includes(s.key));

  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
      }}>
        <label style={{
          fontSize: '14px',
          fontWeight: '600',
          color: 'var(--text-primary)',
        }}>
          Custom Flags
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          {availableSuggestions.length > 0 && (
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setShowSuggestions(!showSuggestions)}
                style={{
                  padding: '4px 12px',
                  fontSize: '12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                Suggestions
                <ChevronDown size={14} />
              </button>
              
              {showSuggestions && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  minWidth: '250px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                }}>
                  {availableSuggestions.map((suggestion, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleSuggestionClick(suggestion)}
                      style={{
                        padding: '12px',
                        cursor: 'pointer',
                        borderBottom: idx < availableSuggestions.length - 1 ? '1px solid var(--border-color)' : 'none',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                        marginBottom: '2px',
                      }}>
                        -{suggestion.key}: {suggestion.value}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: 'var(--text-secondary)',
                      }}>
                        {suggestion.description}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={handleAddFlag}
            style={{
              padding: '4px 12px',
              fontSize: '12px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: '#3b82f6',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <Plus size={14} />
            Add Flag
          </button>
        </div>
      </div>
      
      {flagsArray.length === 0 ? (
        <div style={{
          padding: '24px',
          textAlign: 'center',
          color: 'var(--text-secondary)',
          fontSize: '13px',
          border: '1px dashed var(--border-color)',
          borderRadius: '6px',
        }}>
          No custom flags added. Click "Add Flag" or use suggestions.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {flagsArray.map((flag, index) => (
            <div key={index} style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
            }}>
              <input
                type="text"
                placeholder="flag-name"
                value={flag.key}
                onChange={(e) => handleKeyChange(index, e.target.value)}
                style={{
                  flex: '1',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  fontSize: '13px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                }}
              />
              {getInputType(flag.key) === 'textarea' ? (
                <textarea
                  placeholder={getPlaceholder(flag.key)}
                  value={flag.value}
                  onChange={(e) => handleValueChange(index, e.target.value)}
                  rows={flag.key === 'prompt' ? 4 : 3}
                  style={{
                    flex: '1',
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    fontSize: '13px',
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    fontFamily: flag.key === 'output_data' ? 'monospace' : 'inherit',
                    resize: 'vertical',
                    minHeight: '60px',
                  }}
                />
              ) : (
                <input
                  type={getInputType(flag.key)}
                  placeholder={getPlaceholder(flag.key)}
                  value={flag.value}
                  onChange={(e) => handleValueChange(index, e.target.value)}
                  style={{
                    flex: '1',
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    fontSize: '13px',
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                  }}
                />
              )}
              <button
                type="button"
                onClick={() => handleRemoveFlag(index)}
                style={{
                  padding: '8px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {flagsArray.length > 0 && (
        <div style={{
          marginTop: '8px',
          padding: '8px 12px',
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '4px',
          fontSize: '12px',
          color: 'var(--text-secondary)',
          fontFamily: 'monospace',
        }}>
          Command preview: {tool} {flagsArray.map(f => {
            if (!f.key) return '';
            if (!f.value || f.value === '') return `-${f.key}`;
            return `-${f.key} ${f.value}`;
          }).filter(Boolean).join(' ')}
        </div>
      )}
    </div>
  );
};

export default CustomFlagsInput;

